(() => {
  'use strict';

  const analytics = () => window.AVAnalytics;
  const customEventsEnabled = () => document.body?.dataset.avCustomEvents === 'true';
  const consentAllowed = () => {
    try {
      return analytics()?.consentAllowed?.() === true;
    } catch (_) {
      return false;
    }
  };

  const emit = (eventName, payload, dedupeKey) => {
    if (!customEventsEnabled() || !consentAllowed() || typeof analytics()?.custom !== 'function') return false;
    return analytics().custom(eventName, payload, { dedupeKey, ttl: 1800 });
  };

  const contextFrom = (node) => {
    const story = node?.closest?.('[data-av-customer-story]');
    const root = node?.closest?.('[data-av-customer-world], [data-av-social-proof-root]');
    return {
      placement: story?.dataset.avPlacement || root?.dataset.avPlacement || 'storefront',
      section_id: root?.dataset.avSectionId || '',
      story_id: story?.dataset.avStoryId || '',
      product_handle: story?.dataset.avProductHandle || node?.dataset?.avProductHandle || '',
      product_world: story?.dataset.avProductWorld || '',
      routine_moment: story?.dataset.avRoutineMoment || '',
      source_type: story?.dataset.avSourceType || '',
      verified: story?.dataset.avVerified === 'true',
      card_position: Number(story?.dataset.avCardPosition || 0),
      locale: story?.dataset.avLocale || document.documentElement.lang || ''
    };
  };

  const observeImpression = (root) => {
    if (root.dataset.avSocialProofObserved === 'true' || !customEventsEnabled() || !consentAllowed()) return;
    root.dataset.avSocialProofObserved = 'true';
    const fire = () => emit('australia:social_proof_impression', {
      placement: root.dataset.avPlacement || 'storefront',
      section_id: root.dataset.avSectionId || '',
      result_count: Number(root.dataset.avStoryCount || root.querySelectorAll('[data-av-customer-story]').length || 0)
    }, `australia:social_proof_impression:${location.pathname}:${root.dataset.avSectionId || root.dataset.avPlacement || 'storefront'}`);
    if (!('IntersectionObserver' in window)) {
      fire();
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= .3)) return;
      fire();
      observer.disconnect();
    }, { threshold: [.3] });
    observer.observe(root);
  };

  const bindFilters = (root) => {
    const filters = root.querySelector('[data-av-routine-filters]');
    if (!filters || filters.dataset.avCustomerWorldBound === 'true') return;
    filters.dataset.avCustomerWorldBound = 'true';
    filters.addEventListener('click', (event) => {
      const button = event.target.closest('[data-av-routine-filter]');
      if (!button) return;
      const selected = button.dataset.avRoutineFilter || 'all';
      filters.querySelectorAll('[data-av-routine-filter]').forEach((candidate) => {
        candidate.setAttribute('aria-pressed', String(candidate === button));
      });
      root.querySelectorAll('[data-av-customer-story]').forEach((card) => {
        const item = card.closest('[data-av-customer-story-item]') || card;
        item.hidden = selected !== 'all' && card.dataset.avRoutineMoment !== selected;
      });
      emit('australia:social_proof_filter', {
        placement: root.dataset.avPlacement || 'storefront',
        section_id: root.dataset.avSectionId || '',
        routine_moment: selected
      }, `australia:social_proof_filter:${location.pathname}:${root.dataset.avSectionId || ''}:${selected}`);
    });
  };

  const bindInteractions = (root) => {
    if (root.dataset.avCustomerWorldBound === 'true') return;
    root.dataset.avCustomerWorldBound = 'true';

    root.addEventListener('toggle', (event) => {
      const details = event.target.closest?.('[data-av-story-details]');
      if (!details?.open || details.dataset.avStoryOpenTracked === 'true') return;
      details.dataset.avStoryOpenTracked = 'true';
      const context = contextFrom(details);
      emit('australia:customer_story_open', context, `australia:customer_story_open:${location.pathname}:${context.story_id}`);
    }, true);

    root.addEventListener('click', (event) => {
      const productLink = event.target.closest('[data-av-story-product-link]');
      if (!productLink) return;
      const context = contextFrom(productLink);
      emit('australia:customer_story_product_click', context, `australia:customer_story_product_click:${location.pathname}:${context.story_id}:${context.product_handle}`);
    });

    root.querySelectorAll('[data-av-customer-media]').forEach((media) => {
      media.addEventListener('play', () => {
        if (media.dataset.avMediaPlayTracked === 'true') return;
        media.dataset.avMediaPlayTracked = 'true';
        const context = contextFrom(media);
        emit('australia:customer_media_play', context, `australia:customer_media_play:${location.pathname}:${context.story_id}`);
      }, { once: true });
    });
  };

  const bindReviewCtas = (scope) => {
    scope.querySelectorAll?.('[data-av-review-cta]').forEach((link) => {
      if (link.dataset.avReviewCtaBound === 'true') return;
      link.dataset.avReviewCtaBound = 'true';
      link.addEventListener('click', () => {
        const context = contextFrom(link);
        emit('australia:review_cta_click', context, `australia:review_cta_click:${location.pathname}:${context.product_handle}:${context.placement}`);
      });
    });
  };

  const init = (scope = document) => {
    const roots = [
      ...(scope.matches?.('[data-av-customer-world], [data-av-social-proof-root]') ? [scope] : []),
      ...(scope.querySelectorAll?.('[data-av-customer-world], [data-av-social-proof-root]') || [])
    ];
    roots.forEach((root) => {
      bindFilters(root);
      bindInteractions(root);
      observeImpression(root);
    });
    bindReviewCtas(scope);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('visitorConsentCollected', () => init(document));
})();
